import { computed } from 'vue'
import { extractRate, MARKET_POSITION_THRESHOLD } from '@/components/rateInsights/utils.js'

export function useMarketMetrics({ rates, accommodationId, visibleCompetitors, selectedChannels, customFilters }) {
  const rateType = computed(() => {
    const room = customFilters.value.roomType || 'double'
    const breakfast = customFilters.value.breakfast
    return `${room}${breakfast === 'included' ? 'Breakfast' : ''}`
  })

  const marketRates = computed(() => customFilters.value.marketRates || 'LOWEST_ALL')

  const yourAvgRate = computed(() => {
    const allRates = rates.value
    if (!allRates || !accommodationId.value) return null
    const channels = selectedChannels.value || []
    const hotelId = String(accommodationId.value)
    const dailyRates = []

    for (const timestamp of Object.keys(allRates)) {
      const rate = extractRate(allRates[timestamp]?.[hotelId], channels, rateType.value, marketRates.value)
      if (rate && rate > 0) dailyRates.push(rate)
    }

    if (!dailyRates.length) return null
    return Math.round(dailyRates.reduce((sum, r) => sum + r, 0) / dailyRates.length)
  })

  const marketAvg = computed(() => {
    const allRates = rates.value
    if (!allRates || !visibleCompetitors.value?.length) return null
    const channels = selectedChannels.value || []
    const hotelId = String(accommodationId.value)
    const competitors = visibleCompetitors.value.filter(c => String(c.id) !== hotelId)
    if (!competitors.length) return null

    const dailyAvgs = []

    for (const timestamp of Object.keys(allRates)) {
      const dayRates = competitors
        .map(c => extractRate(allRates[timestamp]?.[String(c.id)], channels, rateType.value, marketRates.value))
        .filter(r => r && r > 0)

      if (dayRates.length) {
        dailyAvgs.push(dayRates.reduce((sum, r) => sum + r, 0) / dayRates.length)
      }
    }

    if (!dailyAvgs.length) return null
    return Math.round(dailyAvgs.reduce((sum, r) => sum + r, 0) / dailyAvgs.length)
  })

  const priceDiff = computed(() => {
    if (yourAvgRate.value === null || marketAvg.value === null) return null
    return yourAvgRate.value - marketAvg.value
  })

  const priceDiffPct = computed(() => {
    if (priceDiff.value === null || marketAvg.value === null) return null
    return Math.round((priceDiff.value / marketAvg.value) * 100)
  })

  const marketPositionStatus = computed(() => {
    if (priceDiffPct.value === null) return null
    if (priceDiffPct.value > MARKET_POSITION_THRESHOLD) return 'above_market'
    if (priceDiffPct.value < -MARKET_POSITION_THRESHOLD) return 'below_market'
    return 'well_positioned'
  })

  /** True when more than 50% of the selected competitors have no valid rate data for the period. */
  const hasLowMarketData = computed(() => {
    const allRates = rates.value
    if (!allRates) return false
    const channels = selectedChannels.value || []
    const hotelId = String(accommodationId.value)

    const competitorIds = (visibleCompetitors.value || []).map(c => String(c.id)).filter(id => id !== hotelId)
    if (!competitorIds.length) return false

    const competitorsWithData = competitorIds.filter(id =>
      Object.keys(allRates).some(timestamp =>
        extractRate(allRates[timestamp]?.[id], channels, rateType.value, marketRates.value)
      )
    )

    return competitorsWithData.length < competitorIds.length / 2
  })

  return {
    yourAvgRate,
    marketAvg,
    priceDiff,
    priceDiffPct,
    marketPositionStatus,
    hasLowMarketData,
  }
}
